import InPlaceError from "components/in-place-error/InPlaceError";
import React, { useState, MouseEventHandler, useEffect } from "react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./ssn.scss";
import { globalLinkNoTxClass } from "utilities/globalLinkNoTx";
import { formatPastedSSN, handleSSNKeyDown } from "./ssnInputBehavior";

export const Label = ({ label }: { label: string }) => {
  return (
    <div className="ssn-label__wrapper">
      <label htmlFor="label" className="ssn-label">
        {label}
      </label>
    </div>
  );
};

export const Icon = ({
  icon,
  isIconPointer,
  onIconClick,
}: {
  icon: IconDefinition;
  isIconPointer?: boolean;
  onIconClick?: MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <button
      type="button"
      className={`ssn--icon ${isIconPointer ? "ssn--icon--pointer" : ""}`}
      onClick={onIconClick}
      aria-label="Toggle SSN visibility"
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
};

export const eyeIcon = (isSSNVisible: boolean) =>
  isSSNVisible ? regular("eye") : regular("eye-slash");

export interface SSNProps {
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  initialValue?: string;
  onChange?: (e: string) => void;
  onBlur?(e: React.FormEvent<HTMLInputElement>): void;
}

const MASKED_SSN_PATTERN = /^X{3}-X{2}-X{4}$/i;

export const isMaskedSSN = (value: string): boolean =>
  MASKED_SSN_PATTERN.test(value);

export const normalizeSSNDigits = (value: string): string =>
  value.replace(/\D/g, "").slice(0, 9);

export const format = (rawSSN: string, maskSSNWithX: boolean) => {
  if (isMaskedSSN(rawSSN)) {
    return maskSSNWithX ? rawSSN.replace(/X/gi, "x") : rawSSN;
  }

  const digits = normalizeSSNDigits(rawSSN);
  let formattedSSN = digits;

  if (digits.length > 3 && digits.length <= 5) {
    formattedSSN = `${digits.slice(0, 3)}-${digits.slice(3)}`;
  } else if (digits.length > 5) {
    formattedSSN = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }

  return maskSSNWithX
    ? formattedSSN.replace(/\d/g, "x")
    : formattedSSN;
};

const Ssn = ({
  label,
  errorMessage,
  placeholder,
  isReadOnly,
  isDisabled,
  initialValue,
  onChange,
  onBlur,
}: SSNProps) => {
  const [ssn, setSSN] = useState("");
  const [isSSNVisible, setSSNVisible] = useState(false);

  useEffect(() => {
    if (initialValue) {
      setSSN(
        isMaskedSSN(initialValue) ? initialValue : format(initialValue, false)
      );
    }
  }, [initialValue]);

  const updateSSN = (nextValue: string) => {
    const formatted = isMaskedSSN(nextValue)
      ? nextValue
      : format(nextValue, false);
    setSSN(formatted);
    onChange?.(formatted);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isSSNVisible) {
      return;
    }
    updateSSN(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleSSNKeyDown(e, ssn, updateSSN, isSSNVisible);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    updateSSN(formatPastedSSN(ssn, e.clipboardData.getData("text")));
  };

  return (
    <div className="ssn-container">
      {label && <Label label={label} />}
      <div className="ssn__wrapper">
        <input
          className={globalLinkNoTxClass(errorMessage ? "ssn ssn--error" : "ssn")}
          type="text"
          id="ssn"
          name="ssn"
          value={isSSNVisible ? ssn : format(ssn, true)}
          placeholder={placeholder}
          autoComplete="off"
          onInput={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={isDisabled}
          readOnly={isReadOnly}
          onBlur={onBlur}
          spellCheck={false}
          inputMode="numeric"
          aria-label={label}
          data-testid="pii-ssn-input"
        />
        <Icon
          icon={eyeIcon(isSSNVisible)}
          isIconPointer
          onIconClick={() => setSSNVisible(!isSSNVisible)}
        />
        {errorMessage && <InPlaceError errorPhrase={errorMessage} />}
      </div>
    </div>
  );
};

export default Ssn;
